// WindSound
// Features:
// - Global ambient wind sound with distance-based volume
// - Smooth fade-in on activation
// - Reacts to global sound toggle and phase changes

import { useEffect, useRef } from 'react'
import { useThree, useFrame, useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import gsap from 'gsap'

import { useStore } from '../store/useStore'
import { usePhase, PHASES } from '../store/usePhase'

const WIND_CONFIG = {
    soundUrl: 'sounds/wind.mp3',
    roomCenter: new THREE.Vector3(0, 2, 0),
    maxDistance: 15,
    baseVolume: 0.35,
    minVolume: 0.005,
    fadeInSec: 2.0,
}

export default function WindSound({
    activePhase = PHASES.introTravel,
    ...props
}) {
    const { camera } = useThree()
    const isSoundOn = useStore((s) => s.isSoundOn)
    const phase = usePhase((s) => s.phase)

    // Merge defaults
    const config = { ...WIND_CONFIG, ...props }

    // Audio resources
    const buffer = useLoader(THREE.AudioLoader, config.soundUrl)
    const listenerRef = useRef(null)
    const soundRef = useRef(null)
    const gainNodeRef = useRef(null)

    // Fade state
    const fadeRef = useRef({ f: 0 })

    // 1. Frame Loop: Distance-based volume
    useFrame(() => {
        const gainNode = gainNodeRef.current
        if (!gainNode) return

        if (!isSoundOn) {
            gainNode.gain.value = 0
            return
        }

        const dist = camera.position.distanceTo(config.roomCenter)
        const t = THREE.MathUtils.clamp(dist / config.maxDistance, 0, 1)
        // Smoothstep
        const distanceFactor = t * t * (3 - 2 * t)

        const targetVol = THREE.MathUtils.lerp(
            config.minVolume,
            config.baseVolume,
            distanceFactor
        )

        gainNode.gain.value = targetVol * fadeRef.current.f
    })

    // Helper: Fade In
    const fadeIn = () => {
        const gainNode = gainNodeRef.current
        if (!gainNode) return

        // Reset gain ramp
        gainNode.gain.cancelScheduledValues(0)

        // Animate scalar
        fadeRef.current.f = 0
        gsap.to(fadeRef.current, {
            f: 1,
            duration: config.fadeInSec,
            ease: 'sine.inOut',
            overwrite: 'auto',
        })
    }

    // 2. Setup / Teardown
    useEffect(() => {
        if (!buffer) return

        // Create listener & sound
        const listener = new THREE.AudioListener()
        // Check if camera already has listener to avoid warnings?
        // (Assuming this is the only global sound attached this way or unique per scene)
        camera.add(listener)

        const sound = new THREE.Audio(listener)
        sound.setBuffer(buffer)
        sound.setLoop(true)

        const gainNode = sound.getOutput()

        listenerRef.current = listener
        soundRef.current = sound
        gainNodeRef.current = gainNode

        // Start muted to prevent volume glitch before fade/distance update
        gainNode.gain.value = 0
        sound.play()

        // Initial Check
        if (isSoundOn && phase === activePhase) {
            fadeIn()
        }

        return () => {
            // Cleanup
            sound.stop()
            sound.disconnect()
            camera.remove(listener)

            listenerRef.current = null
            soundRef.current = null
            gainNodeRef.current = null
        }
    }, [buffer, camera])

    // 3. Phase Change Reaction
    useEffect(() => {
        if (phase === activePhase && isSoundOn) {
            fadeIn()
        }
    }, [phase, isSoundOn, activePhase])

    return null
}
